import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle, Trash2, ScrollText, Play, Pause, User } from "lucide-react";

export default function AdminRules() {
  const { authAxios } = useAuth();
  const [description, setDescription] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [preview, setPreview] = useState(null); // { interpreted, error }
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRules = useCallback(async () => {
    try {
      const r = await authAxios.get("/rules");
      setRules(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento regole");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const handleInterpret = async () => {
    if (interpreting) return;
    setInterpreting(true);
    setPreview(null);
    try {
      const r = await authAxios.post("/rules/interpret", { description });
      if (r.data.ok) {
        setPreview({ interpreted: r.data.interpreted });
      } else {
        setPreview({ error: r.data.error });
      }
    } catch (err) {
      setPreview({ error: err.response?.data?.detail || "Errore" });
    } finally {
      setInterpreting(false);
    }
  };

  const handleSave = async () => {
    if (saving || !preview?.interpreted) return;
    setSaving(true);
    try {
      await authAxios.post("/rules", { description, interpreted: preview.interpreted });
      toast.success("Regola salvata e attiva");
      setDescription("");
      setPreview(null);
      await loadRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    const newActive = !rule.active;
    // Optimistic
    setRules(prev => prev.map(r => r.id === rule.id ? { ...r, active: newActive } : r));
    try {
      await authAxios.put(`/rules/${rule.id}/toggle`, { active: newActive });
    } catch (err) {
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, active: !newActive } : r));
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Eliminare la regola "${rule.description}"?`)) return;
    try {
      await authAxios.delete(`/rules/${rule.id}`);
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success("Regola eliminata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const formatPayload = (interpreted) => {
    if (!interpreted) return "";
    if (interpreted.type === "giri_visibility") {
      const giri = interpreted.payload?.allowed_giri || [];
      return `Giri consentiti: ${giri.map(g => `${g.cod_agente}-${g.cod_giorno}`).join(", ") || "nessuno"}`;
    }
    return JSON.stringify(interpreted.payload || {});
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 max-w-3xl mx-auto">
        <PageHeader title="Regole" />

        <h1 className="font-heading text-2xl font-bold text-white mb-1" data-testid="rules-title">
          Regole (AI)
        </h1>
        <p className="text-zinc-400 text-sm mb-4">
          Scrivi una regola in italiano. L'AI la interpreta e la applica automaticamente.
        </p>

        {/* Composer */}
        <div className="p-4 rounded-xl border border-white/10 bg-[#18181b] mb-4">
          <label className="text-zinc-300 text-xs font-medium mb-1.5 block">
            Descrizione della regola
          </label>
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => { setDescription(e.target.value); setPreview(null); }}
            placeholder={'Esempio: "L\'agente tony deve vedere solo il giro 3-1"'}
            className="bg-[#09090b] border-white/10 text-sm resize-none"
            data-testid="rule-description-input"
          />
          <div className="flex items-center gap-2 mt-3">
            <Button
              onClick={handleInterpret}
              disabled={interpreting || description.trim().length < 3}
              className="bg-violet-500 hover:bg-violet-600 text-white"
              data-testid="interpret-rule-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {interpreting ? "Interpreto..." : "Interpreta con AI"}
            </Button>
          </div>

          {/* Preview */}
          {preview?.error && (
            <div className="mt-3 p-3 rounded-lg border border-red-500/30 bg-red-500/5 flex items-start gap-2" data-testid="rule-preview-error">
              <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{preview.error}</p>
            </div>
          )}
          {preview?.interpreted && (
            <div className="mt-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-testid="rule-preview-ok">
              <div className="flex items-start gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-emerald-200 text-sm font-medium">{preview.interpreted.summary}</p>
                  <p className="text-zinc-400 text-xs mt-1">
                    Tipo: <span className="font-mono">{preview.interpreted.type}</span>
                  </p>
                  <p className="text-zinc-400 text-xs">
                    Utente: <span className="font-mono">{preview.interpreted.target?.user_label}</span>
                  </p>
                  <p className="text-zinc-400 text-xs">{formatPayload(preview.interpreted)}</p>
                </div>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white mt-2"
                data-testid="save-rule-btn"
              >
                {saving ? "Salvo..." : "Conferma e attiva regola"}
              </Button>
            </div>
          )}
        </div>

        {/* Existing rules */}
        <h2 className="font-heading text-lg font-bold text-white mb-2 flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-zinc-400" />
          Regole esistenti ({rules.length})
        </h2>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-violet-500 mx-auto" />
          </div>
        ) : rules.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-8">Nessuna regola salvata</p>
        ) : (
          <div className="space-y-2" data-testid="rules-list">
            {rules.map((r) => (
              <div
                key={r.id}
                data-testid={`rule-${r.id}`}
                className={`p-3 rounded-lg border bg-[#18181b] ${
                  r.active ? "border-emerald-500/20" : "border-white/5 opacity-60"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{r.interpreted?.summary || r.description}</p>
                    <p className="text-zinc-500 text-[11px] mt-0.5 italic">"{r.description}"</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-400">
                      {r.interpreted?.target?.user_label && (
                        <span className="inline-flex items-center gap-1">
                          <User className="w-3 h-3" />{r.interpreted.target.user_label}
                        </span>
                      )}
                      <span>{formatPayload(r.interpreted)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => toggleRule(r)}
                      title={r.active ? "Disattiva" : "Attiva"}
                      data-testid={`toggle-rule-${r.id}`}
                      className={`p-1.5 rounded-md border ${
                        r.active
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                          : "border-white/10 bg-white/5 text-zinc-400 hover:bg-white/10"
                      }`}
                    >
                      {r.active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => deleteRule(r)}
                      title="Elimina"
                      data-testid={`delete-rule-${r.id}`}
                      className="p-1.5 rounded-md border border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/15"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
